import type { LaunchInfo } from './launch';

/**
 * RUN DAY (Halloween, launch.json "event"): one day of scheduled races. Pure schedule math so the panel, the race bar
 * and the rules all agree, and so it can be tested without a clock.
 */
export type RunDayEvent = NonNullable<LaunchInfo['event']>;

export const DEFAULT_RACE_MINUTES = 13;

/** The Halloween event from /launch.json, or null when the flag is off or malformed. */
export function halloweenEvent(info: LaunchInfo | null | undefined): RunDayEvent | null {
  const e = info?.event;
  if (!e || e.kind !== 'halloween' || !/^\d{4}-\d{2}-\d{2}$/.test(e.date) || !e.times?.length) return null;
  return runDayStarts(e).length ? e : null;
}

/** Race start times (ms, ascending) for the event's UTC date and "HH:MM" UTC slots. */
export function runDayStarts(e: Pick<RunDayEvent, 'date' | 'times'>): number[] {
  return e.times
    .map((t) => Date.parse(`${e.date}T${t.padStart(5, '0')}:00Z`))
    .filter(Number.isFinite)
    .sort((a, b) => a - b);
}

export type RunDayPhase =
  /** Before the first race. */
  | { kind: 'before'; next: number; index: 0 }
  /** Race `index` (0-based) is within its scheduled window. */
  | { kind: 'live'; index: number; endsAt: number; next: number | null }
  /** Between races; `index` is the next race. */
  | { kind: 'between'; index: number; next: number }
  /** The last race's window has ended. */
  | { kind: 'done' };

/** Where `now` falls in the day. Scheduled windows only: the race bar still reports verified onchain state separately. */
export function runDayPhase(starts: number[], now: number, raceMinutes = DEFAULT_RACE_MINUTES): RunDayPhase {
  const len = raceMinutes * 60_000;
  if (!starts.length) return { kind: 'done' };
  if (now < starts[0]) return { kind: 'before', next: starts[0], index: 0 };
  for (let i = 0; i < starts.length; i++) {
    if (now >= starts[i] && now < starts[i] + len) return { kind: 'live', index: i, endsAt: starts[i] + len, next: starts[i + 1] ?? null };
    if (i + 1 < starts.length && now < starts[i + 1]) return { kind: 'between', index: i + 1, next: starts[i + 1] };
  }
  return { kind: 'done' };
}

/** "2d 04:05:06" / "04:05:06" for a countdown. */
export function countdownText(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(s / 86_400);
  const pad = (n: number) => String(n).padStart(2, '0');
  const hms = `${pad(Math.floor((s % 86_400) / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
  return d > 0 ? `${d}d ${hms}` : hms;
}

/** Whole USD from a 6-decimal (or `decimals`) base-unit string; null when unknown. */
export function usdFromUnits(raw: string | bigint | null | undefined, decimals: number): number | null {
  if (raw === null || raw === undefined || raw === '') return null;
  try {
    return Number(BigInt(raw)) / 10 ** decimals;
  } catch {
    return null;
  }
}
